/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin/organisations/lovs/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----
 * ------------------------------------------------------------------
 */
import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { LocalStateContext } from 'contexts/local-state-context';
import { useBoolean, useTranslation, useAuthorization } from 'hooks';
import { GET_NAMED_ITEMS } from 'queries/common';
import { NamedItemFields } from 'queries/generated/fragments.generated';
import { GET_RESTRICTED_STUDY } from 'queries/studies';
import { NamedItemType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { convertNamedType } from 'utils/convertor';
import NotificationService from 'utils/notification';
import AddLOV from 'views/components/admin/organisations/lovs/add-lov';
import LOVsGrid from 'views/components/admin/organisations/lovs/lovs-grid';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import 'views/App.scss';

const AdminOrganisationLOVs = ({ study }: any) => {
    const { t } = useTranslation();
    const user = useAuthorization();

    const [lovs, setLovs] = useState([]);
    const [loadNamedItem, { loading, data, error }] = useLazyQuery(GET_NAMED_ITEMS);
    const [loadStudyData, { loading: loadingStudy, data: studyData }] = useLazyQuery(GET_RESTRICTED_STUDY);
    const { state: localState } = useContext(LocalStateContext);
    const { organisationId } = user;
    const [studyFunctionIds, setStudyFunctionIds] = useState([]);
    const canEdit = user?.canWriteSystemAdminLevel();

    const getLovs = (itemsData: any) =>
        itemsData?.namedItems
            .filter((item: NamedItemFields) => item.studyId === Constants.ObjectId.Empty)
            .map((item: NamedItemFields) => ({ ...item, typeLabel: convertNamedType(item.type) }))
            .filter(
                (item: NamedItemFields) =>
                    item.type !== NamedItemType.DETECTION_TYPE && item.type !== NamedItemType.SM_REVIEW_TYPE,
            ) || [];

    useEffect(() => {
        loadNamedItem();
    }, [localState.selectedOrganisation]);

    useEffect(() => {
        if (study) loadStudyData({ variables: { id: study.id } });
    }, [study]);

    useEffect(() => {
        if (studyData?.study) setStudyFunctionIds(studyData.study.studyFunctionIds);
    }, [studyData]);

    useEffect(() => {
        const readyLovs = getLovs(data);
        const newLovs = readyLovs.map((item: any) => ({
            ...item,
            comments: item.comments || '',
            _lovDescription: t(`admin:namedTypeInfo-${item.type}`),
        }));

        setLovs(newLovs);
    }, [data]);

    const showAddLOV = useBoolean(false);

    const [currentLOV, setCurrentLOV] = useState(null);

    if (loading || loadingStudy) return <Loading />;

    if (error) return <Error error={error} />;

    return (
        <>
            <AddLOV
                lov={currentLOV}
                lovs={lovs ?? []}
                studyId={study?.id ?? Constants.ObjectId.Empty}
                studyFunctionIds={studyFunctionIds}
                open={showAddLOV.value}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add') NotificationService.showNotification(t('admin:LOVsuccessfulCreated'));
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:LOVsuccessfulUpdated', {
                                    id: currentLOV.recordId,
                                }),
                            );

                        setCurrentLOV(null);
                        showAddLOV.setFalse();
                    }
                }}
                organisationId={organisationId}
            />
            <PageTitle text={t('admin:manageLOV')} />
            <br />
            <Button
                text="admin:addLOV"
                onClick={() => {
                    showAddLOV.setTrue();
                }}
                className="main-button"
                disabled={!canEdit}
                style={{ marginLeft: 'auto', display: 'flex', marginTop: 20, marginBottom: 20 }}
            />
            <LOVsGrid
                lovs={lovs}
                rowActions={(lov: NamedItemFields) => [
                    {
                        label: 'admin:editLOV',
                        icon: ['fal', 'edit'],
                        enabled: canEdit,
                        onClick: () => {
                            setCurrentLOV(lov);
                            showAddLOV.setTrue();
                        },
                    },
                ]}
            />
        </>
    );
};

export default AdminOrganisationLOVs;
